import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';

type LegalPage = 'privacy' | 'terms';

interface LegalOverlayProps {
  page: LegalPage | null;
  onClose: () => void;
}

export default function LegalOverlay({ page, onClose }: LegalOverlayProps) {
  useEffect(() => {
    if (page) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [page]);

  return (
    <AnimatePresence>
      {page && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/80 backdrop-blur-sm overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-3xl mx-4 my-8 md:my-16 bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-10 py-5 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/10">
              <h1 className="text-lg md:text-xl font-bold text-white tracking-tight">
                {page === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
              </h1>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 md:px-10 py-8 md:py-10">
              {page === 'privacy' ? <PrivacyContent /> : <TermsContent />}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-white font-bold text-base md:text-lg mt-8 mb-3 tracking-tight">{children}</h2>
);

const Para = ({ children }: { children: React.ReactNode }) => (
  <p className="text-white/60 text-sm leading-relaxed mb-4">{children}</p>
);

const BulletList = ({ items }: { items: string[] }) => (
  <ul className="list-disc list-inside text-white/60 text-sm leading-relaxed mb-4 space-y-1.5 pl-1">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

function PrivacyContent() {
  return (
    <div>
      <Para>
        <span className="text-white/40 text-xs uppercase tracking-widest">Last updated: May 2026</span>
      </Para>

      <Para>
        Voqals MediaTech Pvt. Ltd. ("Voqals," "we," "us," or "our") operates the website voqals.com (the "Site"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Site or engage with our services. Please read this policy carefully. By using the Site, you agree to the practices described herein.
      </Para>

      <SectionTitle>1. Information We Collect</SectionTitle>
      <Para>We collect information that you voluntarily provide when you interact with our Site:</Para>
      <BulletList items={[
        'Contact information: name, email address, company name',
        'Project details: model requirements, use case descriptions, language and volume specifications',
        'Vendor information: studio name, acoustic specifications, equipment details',
        'File uploads: project briefs, audio samples, or related documents submitted through our forms',
        'Communication records: messages sent through our contact forms',
      ]} />
      <Para>
        We also automatically collect a limited set of technical information when you visit the Site, including browser type and version, operating system, referring URL, pages viewed, and approximate location derived from your IP address. This information is collected through cookies and similar technologies (see Section 3) and is used in aggregate for measurement and security purposes.
      </Para>

      <SectionTitle>2. How We Use Your Information</SectionTitle>
      <Para>We use the information we collect for the following purposes:</Para>
      <BulletList items={[
        'To respond to your enquiries and communicate with you about our services',
        'To scope and plan custom data collection projects based on your requirements',
        'To evaluate vendor and studio partnership applications',
        'To process dataset licensing requests',
        'To send you updates about your project or enquiry (we will never send unsolicited marketing emails)',
        'To measure the effectiveness of our advertising and improve our Site',
        'To detect, prevent, and address security incidents or fraudulent activity',
      ]} />

      <SectionTitle>3. Cookies and Tracking Technologies</SectionTitle>
      <Para>
        We use a limited set of cookies and similar technologies. We group them into two categories:
      </Para>
      <BulletList items={[
        'Strictly necessary: required for the Site to function — e.g., remembering whether you have accepted this cookie notice. These cannot be disabled.',
        'Analytics and advertising: provided by Google Ads (gtag.js) for conversion measurement of campaigns we run. These are loaded only after you accept cookies via our consent banner. Google may set cookies under the .voqals.com and googleadservices.com domains.',
      ]} />
      <Para>
        We do not use cookies for cross-site retargeting outside Google Ads, and we do not sell your data to advertising networks. You can withdraw your cookie consent at any time by clearing your browser cookies for voqals.com — the consent banner will reappear on your next visit. You may also use your browser settings or extensions to block third-party cookies.
      </Para>
      <Para>
        For details on Google's data practices, see Google's Privacy Policy at policies.google.com/privacy.
      </Para>

      <SectionTitle>4. Third-Party Services</SectionTitle>
      <Para>
        Our contact forms are processed through Web3Forms, a third-party form submission service. When you submit a form, your data is transmitted securely to Web3Forms, which forwards it to our email. Web3Forms does not store your submission data beyond delivery. Please refer to Web3Forms' own privacy policy for details on their data handling.
      </Para>
      <Para>
        File uploads submitted through our forms are processed via secure, pre-signed upload URLs. Files are stored temporarily for review and project scoping purposes only.
      </Para>
      <Para>
        Our Site is hosted on Netlify, which may collect standard server logs (IP address, request time, requested resource) for security and operational purposes.
      </Para>

      <SectionTitle>5. Data Storage and Security</SectionTitle>
      <Para>
        We take reasonable administrative, technical, and physical measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. Form submissions are delivered directly to our secure email systems. We do not maintain a public-facing user database, and personal information submitted through forms is held only in our internal communication systems.
      </Para>

      <SectionTitle>6. Data Retention</SectionTitle>
      <Para>
        We retain your information only for as long as necessary to fulfill the purposes outlined in this policy, or as required by applicable law. Project-related communications and details are retained for the duration of our business relationship and a reasonable period thereafter. Cookie-based analytics data is retained according to Google's retention defaults (typically 14 months). You may request deletion of your data at any time by contacting us.
      </Para>

      <SectionTitle>7. Data Sharing</SectionTitle>
      <Para>We do not sell, trade, rent, or share your personal information with third parties for their independent marketing purposes. We may share information only in the following circumstances:</Para>
      <BulletList items={[
        'With your explicit consent',
        'To comply with applicable law, regulation, legal process, or governmental request',
        'To protect the rights, property, or safety of Voqals, our clients, or others',
        'With service providers who assist in operating our business (e.g., Web3Forms for form processing, Google for advertising measurement, Netlify for hosting), each bound by their own data processing terms',
      ]} />

      <SectionTitle>8. International Data Transfers</SectionTitle>
      <Para>
        Voqals is based in India. If you are accessing the Site from outside India, please be aware that your information may be transferred to, stored, and processed in India, and by certain service providers (such as Web3Forms, Google, and Netlify) in jurisdictions outside India, including the United States and the European Union. We rely on standard contractual safeguards offered by these providers for cross-border data transfers.
      </Para>

      <SectionTitle>9. Your Rights</SectionTitle>
      <Para>
        Depending on where you live, you may have the following rights regarding your personal information. We honor these rights regardless of where you are located:
      </Para>
      <BulletList items={[
        'Access — request a copy of the personal information we hold about you',
        'Correction — request that we correct inaccurate or incomplete data',
        'Deletion (right to erasure) — request that we delete your data, subject to legal retention requirements',
        'Restriction or objection — request that we limit or stop processing your data',
        'Portability — receive your data in a structured, machine-readable format',
        'Withdraw consent — for any processing based on consent (such as cookies), at any time',
        'Lodge a complaint — with the data protection authority in your jurisdiction',
      ]} />
      <Para>
        For users in the European Economic Area, United Kingdom, and Switzerland, these rights are granted under the General Data Protection Regulation (GDPR) and equivalent local laws. For users in India, equivalent rights are granted under the Digital Personal Data Protection Act, 2023 (DPDP Act). For users in California, equivalent rights are granted under the California Consumer Privacy Act (CCPA / CPRA).
      </Para>
      <Para>
        To exercise any of these rights, please contact us at enterprise@voqals.com. We will respond to your request within 30 days. We will not discriminate against you for exercising any of these rights.
      </Para>

      <SectionTitle>10. Children's Privacy</SectionTitle>
      <Para>
        Our Site and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.
      </Para>

      <SectionTitle>11. Changes to This Policy</SectionTitle>
      <Para>
        We may update this Privacy Policy from time to time. Any changes will be reflected on this page with an updated "Last updated" date. Material changes will, where reasonable, be brought to your attention through a notice on the Site. We encourage you to review this policy periodically.
      </Para>

      <SectionTitle>12. Contact Us</SectionTitle>
      <Para>
        If you have questions about this Privacy Policy, our data practices, or wish to exercise any of your rights, please contact us at:
      </Para>
      <div className="text-white/60 text-sm leading-relaxed mb-4">
        <p className="text-white font-medium">Voqals MediaTech Pvt. Ltd.</p>
        <p>Email: enterprise@voqals.com</p>
        <p>Website: voqals.com</p>
      </div>
    </div>
  );
}

function TermsContent() {
  return (
    <div>
      <Para>
        <span className="text-white/40 text-xs uppercase tracking-widest">Last updated: May 2026</span>
      </Para>

      <Para>
        These Terms of Service ("Terms") govern your access to and use of the website voqals.com (the "Site") and the services provided by Voqals MediaTech Pvt. Ltd. ("Voqals," "we," "us," or "our"). By accessing or using the Site, you agree to be bound by these Terms. If you do not agree, please do not use the Site.
      </Para>

      <SectionTitle>1. About Voqals</SectionTitle>
      <Para>
        Voqals is an India-based speech data company that provides high-quality, studio-certified Indian language speech datasets and custom data collection services for artificial intelligence applications, including automatic speech recognition (ASR), text-to-speech (TTS), and conversational AI systems.
      </Para>

      <SectionTitle>2. Services</SectionTitle>
      <Para>Our services include, but are not limited to:</Para>
      <BulletList items={[
        'Custom speech data collection and curation for AI training',
        'Licensing of pre-built, ready-to-use Indian speech datasets',
        'Studio certification and quality assurance for audio recording',
        'Script engineering, persona design, and annotation services',
        'Vendor and studio partnership programs',
      ]} />
      <Para>
        Specific service terms, deliverables, pricing, and timelines are agreed upon separately through project scoping discussions and formal agreements between Voqals and the client.
      </Para>

      <SectionTitle>3. Use of the Site</SectionTitle>
      <Para>You agree to use the Site only for lawful purposes and in accordance with these Terms. You agree not to:</Para>
      <BulletList items={[
        'Use the Site in any way that violates applicable local, national, or international law',
        'Submit false, misleading, or fraudulent information through our forms',
        'Attempt to gain unauthorized access to any part of the Site or its systems',
        'Interfere with or disrupt the integrity or performance of the Site',
        'Reproduce, distribute, or create derivative works from the Site content without our written permission',
      ]} />

      <SectionTitle>4. Intellectual Property</SectionTitle>
      <Para>
        All content on the Site — including text, graphics, logos, design, UI elements, audio samples, and visual assets — is the property of Voqals or its licensors and is protected by applicable intellectual property laws. You may not copy, reproduce, distribute, or create derivative works from any Site content without our prior written consent.
      </Para>
      <Para>
        The Voqals name, logo, and all related marks are trademarks of Voqals. Nothing on the Site grants any license or right to use any trademark without our written permission.
      </Para>

      <SectionTitle>5. Dataset Licensing</SectionTitle>
      <Para>
        Access to and use of Voqals datasets is governed by separate dataset license agreements. Browsing dataset information on the Site does not constitute a license grant. All dataset licensing terms — including permitted uses, restrictions, attribution requirements, and pricing — are established through formal agreements between Voqals and the licensee.
      </Para>

      <SectionTitle>6. Custom Data Collection Projects</SectionTitle>
      <Para>
        Submitting a project enquiry or brief through the Site does not create a binding agreement. Custom data collection projects are initiated only upon mutual agreement of scope, deliverables, timeline, and commercial terms through a separate written agreement or statement of work.
      </Para>

      <SectionTitle>7. Vendor and Studio Partnerships</SectionTitle>
      <Para>
        Submitting a vendor or studio application through the Site does not guarantee partnership or engagement. All vendor relationships are subject to Voqals' studio certification standards and separate partnership agreements.
      </Para>

      <SectionTitle>8. Disclaimer of Warranties</SectionTitle>
      <Para>
        The Site and its content are provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied. Voqals makes no warranties regarding the accuracy, completeness, reliability, or availability of the Site. We do not warrant that the Site will be uninterrupted, error-free, or free of harmful components.
      </Para>

      <SectionTitle>9. Limitation of Liability</SectionTitle>
      <Para>
        To the maximum extent permitted by applicable law, Voqals and its directors, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Site, including but not limited to loss of profits, data, business opportunities, or goodwill, even if we have been advised of the possibility of such damages.
      </Para>

      <SectionTitle>10. Indemnification</SectionTitle>
      <Para>
        You agree to indemnify, defend, and hold harmless Voqals and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses arising out of or in any way connected with your access to or use of the Site or violation of these Terms.
      </Para>

      <SectionTitle>11. Third-Party Links</SectionTitle>
      <Para>
        The Site may contain links to third-party websites or services. Voqals is not responsible for the content, privacy practices, or availability of these external sites. Inclusion of any link does not imply endorsement by Voqals.
      </Para>

      <SectionTitle>12. Governing Law and Jurisdiction</SectionTitle>
      <Para>
        These Terms are governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of the Site shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra, India.
      </Para>

      <SectionTitle>13. Changes to These Terms</SectionTitle>
      <Para>
        We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated "Last updated" date. Your continued use of the Site after any changes constitutes acceptance of the revised Terms.
      </Para>

      <SectionTitle>14. Severability</SectionTitle>
      <Para>
        If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that the remaining Terms remain in full force and effect.
      </Para>

      <SectionTitle>15. Contact Us</SectionTitle>
      <Para>
        If you have questions about these Terms, please contact us at:
      </Para>
      <div className="text-white/60 text-sm leading-relaxed mb-4">
        <p className="text-white font-medium">Voqals MediaTech Pvt. Ltd.</p>
        <p>Email: enterprise@voqals.com</p>
        <p>Website: voqals.com</p>
      </div>
    </div>
  );
}
